import React from "react";
import { Card, Button, Badge, Tag } from "antd";
import {
  HeartOutlined,
  EyeOutlined,
  ShoppingCartOutlined,
} from "@ant-design/icons";
import Rating from "react-rating";
import styled from "styled-components";

const ProductCardWrapper = styled.div`
  .product-card {
    border-radius: 8px;
    overflow: hidden;
    transition: all 0.3s ease;
    position: relative;

    &:hover {
      box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
      transform: translateY(-2px);

      .product-actions {
        opacity: 1;
        visibility: visible;
      }
    }

    .ant-card-cover {
      position: relative;
      overflow: hidden;

      img {
        transition: transform 0.3s ease;
        width: 100%;
        height: 250px;
        object-fit: contain;
      }

      &:hover img {
        transform: scale(1.05);
      }
    }

    .product-badge {
      position: absolute;
      top: 10px;
      left: 10px;
      z-index: 2;
    }

    .discount-badge {
      position: absolute;
      top: 10px;
      right: 10px;
      z-index: 2;
      background: #fd8e07;
      color: white;
      padding: 4px 8px;
      border-radius: 4px;
      font-size: 12px;
      font-weight: bold;
    }

    .product-actions {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      opacity: 0;
      visibility: hidden;
      transition: all 0.3s ease;
      display: flex;
      gap: 10px;

      .action-btn {
        width: 40px;
        height: 40px;
        border-radius: 50%;
        border: none;
        background: white;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
        display: flex;
        align-items: center;
        justify-content: center;

        &:hover {
          background: #000;
          color: white;
        }
      }
    }

    .ant-card-body {
      padding: 15px;

      .product-brand {
        font-size: 12px;
        color: #999;
        margin-bottom: 8px;
        text-transform: capitalize;
        font-weight: 500;
      }

      .product-title {
        font-size: 14px;
        font-weight: 600;
        margin-bottom: 8px;
        color: #333;
        line-height: 1.4;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }

      .product-rating {
        font-size: 12px;
        color: #ffc107;
        margin-bottom: 8px;
        letter-spacing: 2px;
      }

      .product-description {
        font-size: 13px;
        color: #666;
        line-height: 1.4;
        margin-bottom: 12px;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        min-height: 36px;
      }

      .product-colors {
        display: flex;
        gap: 5px;
        margin-bottom: 10px;

        .color-swatch {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          border: 1px solid #ddd;
          cursor: pointer;

          &.gray {
            background: #808080;
          }
          &.beige {
            background: #f5f5dc;
          }
          &.white {
            background: #ffffff;
          }
          &.black {
            background: #000000;
          }
          &.red {
            background: #ff0000;
          }
          &.blue {
            background: #0000ff;
          }
          &.brown {
            background: #a52a2a;
          }
          &.orange {
            background: #ffa500;
          }
          &.green {
            background: #008000;
          }
          &.yellow {
            background: #ffff00;
          }
          &.pink {
            background: #ffc0cb;
          }
        }
      }

      .product-price {
        display: flex;
        align-items: center;
        gap: 8px;
        font-weight: 600;

        .current-price {
          font-size: 16px;
          color: #000;
        }

        .original-price {
          font-size: 13px;
          color: #999;
          text-decoration: line-through;
        }
      }
    }
  }
`;

const ProductCard = ({
  id,
  product,
  image = "https://via.placeholder.com/300x250?text=Product",
  brand = "Brand",
  title = "Product Name",
  price = 0,
  originalPrice = null,
  rating = 0,
  description = "",
  colors = [],
  isNew = false,
  discount = null,
  onClick,
  reviews,
}) => {
  // Support both direct props and product object
  const productData = product || {
    image,
    category: brand,
    name: title,
    price,
    originalPrice,
    rating,
    description,
    colors,
    badge: isNew ? "New" : "",
    discount,
    reviews,
  };

  return (
    <ProductCardWrapper>
      <Card
        className="product-card"
        onClick={onClick}
        style={{ cursor: onClick ? "pointer" : "default" }}
        cover={
          <div>
            <img src={productData.image} alt={productData.name} />
            {productData.badge && (
              <div className="product-badge">
                <Tag
                  color="default"
                  style={{ background: "#fd8e07", color: "#fff" }}
                >
                  {productData.badge}
                </Tag>
              </div>
            )}
            {productData.discount > 0 && (
              <div className="discount-badge">-{productData.discount}%</div>
            )}
          </div>
        }
        styles={{ body: { padding: "15px" } }}
      >
        <div className="product-brand">
          {typeof productData.category === "object"
            ? productData.category?.name
            : productData.category}
        </div>
        <div className="product-title">{productData.name}</div>

        <div className="product-rating">
          <span style={{ marginLeft: "6px", color: "#999", fontSize: "12px" }}>
            {productData.reviews && productData.reviews.length > 0
              ? `${(
                  productData.reviews.reduce((sum, r) => sum + r.rating, 0) /
                  productData.reviews.length
                ).toFixed(1)}`
              : ""}
          </span>
          <Rating
            initialRating={
              productData.reviews && productData.reviews.length > 0
                ? productData.reviews.reduce((sum, r) => sum + r.rating, 0) /
                  productData.reviews.length
                : 0
            }
            readonly
            fractions={2}
            stop={5}
            emptySymbol={
              <span
                style={{ fontSize: "16px", color: "#ddd", marginRight: "1px" }}
              >
                ☆
              </span>
            }
            fullSymbol={
              <span
                style={{
                  fontSize: "16px",
                  color: "#ffc107",
                  marginRight: "1px",
                }}
              >
                ★
              </span>
            }
          />

          <span style={{ marginLeft: "6px", color: "#999", fontSize: "12px" }}>
            ({`${productData.reviews?.length || 0}`})
          </span>
        </div>

        <div className="product-description">
          {productData.short_description}
        </div>

        {colors && colors.length > 0 && (
          <div className="product-colors">
            {colors.map((color, index) => (
              <div
                key={index}
                className={`color-swatch ${color.toLowerCase()}`}
                title={color}
              />
            ))}
          </div>
        )}

        <div className="product-price">
          <span className="current-price">
            ₹
            {(typeof productData.price === "number"
              ? productData.price
              : parseFloat(productData.price || 0)
            ).toLocaleString("en-IN")}
          </span>
        </div>
      </Card>
    </ProductCardWrapper>
  );
};

export default ProductCard;
